import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output, ViewEncapsulation } from "@angular/core";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25ToggleButtonModule } from "../s25-toggle-button/s25.toggle.button.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25TabGroupComponent } from "../../standalone/s25.tab.group.component";
import { AcademicGridStore } from "./s25.academic.grid.store";

@Component({
    selector: "s25-ng-academic-grids-filter",
    template: `
        <h4 class="filters-title">Filters</h4>
        @if (store.filterTabs().length > 1) {
            <s25-ng-tab-group [tabs]="store.filterTabs()" [(chosen)]="store.filterTab" />
        }
        <form class="options" (keydown.enter)="applyFilters()">
            @for (filter of store.filterTab().filters; track filter.label) {
                @if (filter.type !== "none") {
                    <div>
                        <label>{{ filter.label }}</label>
                        @if (filter.type === "text") {
                            <input
                                [(ngModel)]="filter.value"
                                [id]="filter.label + '-filter'"
                                type="text"
                                class="c-input"
                                name="{{ filter.label }}-name"
                                [attr.aria-label]="filter.label"
                            />
                        } @else if (filter.type === "numberRange") {
                            <div class="number-range">
                                <input
                                    [(ngModel)]="filter.value!().min"
                                    type="number"
                                    placeholder="min"
                                    name="{{ filter.label }}-min"
                                    class="c-input"
                                />
                                <span> - </span>
                                <input
                                    [(ngModel)]="filter.value!().max"
                                    type="number"
                                    placeholder="max"
                                    name="{{ filter.label }}-max"
                                    class="c-input"
                                />
                            </div>
                        } @else if (filter.type === "contact") {
                            <s25-contact-dropdown [(value)]="filter.value" [ariaLabel]="filter.label" />
                        } @else if (filter.type === "dropdownMultiselect" || filter.type === "options") {
                            <s25-ng-generic-multiselect-dropdown
                                [items]="filter.data!.items"
                                [value]="filter.value!()"
                                (done)="filter.value!.set($event.slice())"
                                [placeholder]="filter.data!.placeholder"
                                [emptyText]="'None available'"
                                [searchEnabled]="true"
                                [hasSelectAll]="true"
                                [ariaLabel]="'Filter by ' + filter.data!.placeholder"
                            />
                        } @else if (filter.type === "yesNo") {
                            <s25-toggle-button [(value)]="filter.value" />
                        }
                    </div>
                }
            }
        </form>
        <div class="buttons">
            <s25-ng-button [type]="'outline'" [onClick]="applyFilters"> Apply </s25-ng-button>
            <s25-ng-button [type]="'danger--outline'" [onClick]="clearFilters"> Clear </s25-ng-button>
        </div>
    `,
    styles: `
        :host {
            --background: white;
            background: var(--background);

            display: grid;
            gap: 1rem;
            padding: 0.5rem;
        }

        ::ng-deep .nm-party--on s25-ng-academic-grids-filter {
            --background: #3c3d46;
        }

        .filters-title {
            margin: 0;
            font-size: 1.15em;
            text-align: center;
        }

        s25-ng-tab-group {
            justify-content: center;
            font-size: 1em;
        }

        .options {
            display: grid;
            gap: 0.5rem;
            grid-template-columns: 17ch 1fr;
        }

        .options > * {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
            align-items: center;
        }

        .buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: right;
        }

        .number-range {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }

        .number-range input {
            max-width: 5em;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, S25DropdownPaginatedModule, S25ToggleButtonModule, S25ButtonComponent, S25TabGroupComponent],
})
export class S25AcademicGridsFilterComponent {
    readonly store = inject(AcademicGridStore);

    @Output() apply = new EventEmitter<void>();
    @Output() clear = new EventEmitter<void>();

    @Bind
    async applyFilters() {
        await S25Util.delay(0); // Wait for any dropdown to close and emit
        this.apply.emit();
    }

    @Bind
    clearFilters() {
        this.store.clearFilters();
        this.clear.emit();
    }
}
